import {
  Alert,
  Box,
  Button,
  FormControl,
  Grid,
  InputAdornment,
  InputLabel,
  LinearProgress,
  MenuItem,
  Select,
  Snackbar,
  TextField,
  Typography,
  Autocomplete,
  Stack,
} from '@mui/material'
import React, { useContext, useState } from 'react'
import SuccessModal from '../../components/Modals/Success'
import API from '../../services/API'
import validator from 'validator'
import SmallModal from '../../components/Modals/SmallModal'
import { useNavigate } from 'react-router-dom'
import ErrorModal from '../../components/Modals/Error'
import { TravelExplore } from '@mui/icons-material'
import { GlobalContext } from '../../App'
import AddressAutocomplete from '../patientTransport/components/AddressAutocomplete'

const organisationTypes = ['Events', 'PTS', 'Training', 'Cleaning', 'Other']

const AddNewClient = ({
  customCallbackAction,
  enforceType,
  editMode,
  existingClient,
}) => {
  const navigate = useNavigate()
  const { baseLocationOptions } = useContext(GlobalContext)
  const [formValues, setFormValues] = useState(
    existingClient ?? {
      orgName: '',
      orgType: enforceType ?? '',
      orgAddressLineOne: '',
      orgAddressLineTwo: '',
      orgAddressCity: '',
      orgAddressTown: '',
      orgAddressPostCode: '',
      orgWebsite: '',
      orgEmail: '',
      orgTel: '',
      orgDescription: '',
      baseLocation: null,
      financeContact: { name: '', email: '', phone: '' },
    },
  )
  const [uploading, setUploading] = useState(false)
  const [submitSuccess, setSubmitSuccess] = useState(false)
  const [submitError, setSubmitError] = useState(false)
  const [formErrors, setFormErrors] = useState({
    orgEmail: false,
    orgWebsite: false,
    financeContactEmail: false,
    baseLocation: false,
  })
  const [noMatchingGeocode, setNoMatchingGeocode] = useState(false)

  const [contacts, setContacts] = useState([])

  const handleChange = (e) => {
    const { name, value } = e.target
    setFormValues((prev) => ({
      ...prev,
      [name]: value,
    }))
  }

  const selectAddressOption = (option) => {
    setFormValues((prev) => ({
      ...prev,
      orgAddressLineOne: option.addressLine1 || '',
      orgAddressLineTwo: option.addressLine2 || '',
      orgAddressTown: option.town || '',
      orgAddressCity: option.city || '',
      orgAddressPostCode: option.postCode || '',
      location: Array.isArray(option.coordinates)
        ? { type: 'Point', coordinates: option.coordinates }
        : prev.location,
      addressMeta: option.addressMeta,
    }))
  }

  const handleFinanceContactChange = (e) => {
    const { name, value } = e.target
    setFormValues((prev) => ({
      ...prev,
      financeContact: { ...prev.financeContact, [name]: value },
    }))
  }

  const handleSubmit = (e) => {
    setUploading(true)
    e.preventDefault()
    setFormErrors((prev) => ({
      ...prev,
      orgEmail: formValues.orgEmail && !validator.isEmail(formValues.orgEmail),
      orgWebsite:
        formValues.orgWebsite && !validator.isURL(formValues.orgWebsite),
      financeContactEmail:
        formValues.financeContact?.email &&
        !validator.isEmail(formValues.financeContact.email),
    }))
    // Typing a base name without picking it from the dropdown leaves the
    // stored value null while the input still shows text — catch it here
    // with a clear message instead of letting the server reject the save.
    const baseLocationId =
      formValues?.baseLocation?._id || formValues?.baseLocation
    if (!baseLocationId) {
      setFormErrors((prev) => ({ ...prev, baseLocation: true }))
      setUploading(false)
      return
    }
    const afterActions = (res) => {
      if (res.status === 200) {
        !!customCallbackAction
          ? customCallbackAction({
              _id: res.data.newID,
              ...formValues,
            })
          : navigate('/clients/' + res.data.newID)
      } else if (res.data?.errorCode === 'duplicate') {
        setSubmitError('This Organisation Name already Exists.')
      } else if (res.data?.errorCode === 'noMatchingGeocode') {
        setNoMatchingGeocode(true)
      } else {
        setSubmitError(true)
      }
    }
    if (existingClient) {
      API.put('/event-planning/organisations/' + existingClient._id, formValues)
        .then((res) => {
          afterActions(res)
        })
        .finally(() => setUploading(false))
    } else {
      API.post('/event-planning/organisations', formValues)
        .then((res) => {
          afterActions(res)
        })
        .finally(() => setUploading(false))
    }
  }

  return (
    <Grid container component="form" onSubmit={handleSubmit} spacing={2}>
      <Grid item xs={12}>
        <Typography variant="title2">
          {editMode
            ? `Edit Organisation`
            : `Add a New ${enforceType === 'PTS' ? 'Organisation' : 'Client'}`}
        </Typography>
      </Grid>
      <Grid item xs={12} lg={5}>
        <TextField
          value={formValues.orgName}
          onChange={handleChange}
          label="Organisation Name"
          autoComplete="none"
          name="orgName"
          fullWidth
          required
        />
      </Grid>
      <Grid item xs={12} lg={3}>
        <FormControl fullWidth>
          <InputLabel id="orgType-Label">Organisation Type</InputLabel>
          <Select
            labelId="orgType-Label"
            label="Organisation Type"
            value={formValues.orgType}
            onChange={handleChange}
            name="orgType"
            required
            fullWidth
            disabled={enforceType || existingClient}
          >
            {organisationTypes.map((option) => (
              <MenuItem value={option} key={option}>
                {option}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      </Grid>
      <Grid item xs={12} lg={4}>
        <Autocomplete
          options={baseLocationOptions || []}
          // Commit the highlighted match on blur so a typed-but-not-clicked
          // base still becomes a real selection.
          autoSelect
          value={
            baseLocationOptions?.find(
              (baseLocation) =>
                baseLocation._id ===
                (formValues?.baseLocation?._id || formValues?.baseLocation),
            ) || null
          }
          onChange={(event, value) => {
            setFormValues((prev) => ({
              ...prev,
              baseLocation: value?._id || null,
            }))
            setFormErrors((prev) => ({ ...prev, baseLocation: false }))
          }}
          getOptionLabel={(option) => option?.name || ''}
          isOptionEqualToValue={(option, value) => option?._id === value?._id}
          renderInput={(params) => (
            <TextField
              {...params}
              label="Base Location"
              required
              error={!!formErrors.baseLocation}
              helperText={
                formErrors.baseLocation
                  ? 'Pick a base location from the list.'
                  : 'The organisation will only be visible to users with this base location selected.'
              }
            />
          )}
        />
      </Grid>

      {existingClient && formValues?.orgType !== existingClient?.orgType && (
        <Grid item xs={12}>
          <Alert severity="warning">
            Please note that changing an organisation's type will affect their
            availability/visibility (i.e. Event Planning or PTS screens)
          </Alert>
        </Grid>
      )}
      <Grid item xs={12}>
        <AddressAutocomplete
          label="Find Organisation Address"
          address={{
            name: formValues.orgName,
            addressLine1: formValues?.orgAddressLineOne,
            addressLine2: formValues?.orgAddressLineTwo,
            town: formValues?.orgAddressTown,
            city: formValues?.orgAddressCity,
            postCode: formValues?.orgAddressPostCode,
          }}
          onSelect={selectAddressOption}
          helperText="Start typing an address or postcode, then adjust the fields below if needed."
        />
      </Grid>
      <Grid item xs={12} lg={6}>
        <TextField
          value={formValues?.orgAddressLineOne || ''}
          onChange={handleChange}
          label="Address Line 1"
          fullWidth
          required={enforceType === 'PTS'}
          autoComplete="none"
          name="orgAddressLineOne"
        />
      </Grid>
      <Grid item xs={12} lg={6}>
        <TextField
          value={formValues?.orgAddressLineTwo || ''}
          onChange={handleChange}
          label="Address Line 2"
          fullWidth
          autoComplete="none"
          name="orgAddressLineTwo"
        />
      </Grid>
      <Grid item xs={12} lg={6}>
        <TextField
          value={formValues?.orgAddressTown || ''}
          onChange={handleChange}
          label="Town"
          fullWidth
          autoComplete="none"
          name="orgAddressTown"
        />
      </Grid>
      <Grid item xs={12} lg={6}>
        <TextField
          value={formValues?.orgAddressCity || ''}
          onChange={handleChange}
          label="City/County"
          fullWidth
          required={enforceType === 'PTS'}
          autoComplete="none"
          name="orgAddressCity"
        />
      </Grid>
      <Grid item xs={12} lg={3}>
        <TextField
          value={formValues?.orgAddressPostCode || ''}
          onChange={handleChange}
          label="Post Code"
          fullWidth
          required={enforceType === 'PTS'}
          autoComplete="none"
          name="orgAddressPostCode"
        />
      </Grid>
      <Grid item xs={12} lg={4.5}>
        <TextField
          value={formValues.orgEmail}
          onChange={handleChange}
          name="orgEmail"
          label="Organisation Email"
          type="email"
          fullWidth
          required
          autoComplete="none"
          error={!!formErrors.orgEmail}
          helperText={
            (formErrors.orgEmail && 'Not a valid Email Address') ||
            'Falls back to this if no Finance Contact email is set below'
          }
        />
      </Grid>
      <Grid item xs={12} lg={4.5}>
        <TextField
          value={formValues.orgWebsite}
          onChange={handleChange}
          name="orgWebsite"
          label="Website"
          fullWidth
        />
      </Grid>
      <Grid item xs={12}>
        <Stack>
        <Typography variant="title3" fontSize="1rem">
          Finance Contact
        </Typography>
        <Typography variant="caption" color="text.secondary">
          Who invoices and quotes get sent to. Leave blank to use the
          organisation email above instead.
        </Typography></Stack>
      </Grid>
      <Grid item xs={12} lg={4}>
        <TextField
          value={formValues.financeContact?.name || ''}
          onChange={handleFinanceContactChange}
          name="name"
          label="Contact Name"
          fullWidth
          autoComplete="none"
        />
      </Grid>
      <Grid item xs={12} lg={4}>
        <TextField
          value={formValues.financeContact?.email || ''}
          onChange={handleFinanceContactChange}
          name="email"
          label="Finance Email"
          fullWidth
          autoComplete="none"
          error={!!formErrors.financeContactEmail}
          helperText={
            formErrors.financeContactEmail && 'Not a valid Email Address'
          }
        />
      </Grid>
      <Grid item xs={12} lg={4}>
        <TextField
          value={formValues.financeContact?.phone || ''}
          onChange={handleFinanceContactChange}
          name="phone"
          label="Finance Phone"
          fullWidth
          autoComplete="none"
        />
      </Grid>
      <Grid item xs={12}>
        <TextField
          value={formValues.orgDescription}
          onChange={handleChange}
          name="orgDescription"
          label="Description"
          multiline
          minRows={3}
          fullWidth
        />
      </Grid>
      <Grid item xs={12}>
        {uploading && <LinearProgress color="contrast" />}
        <Button
          color="primary"
          variant="contained"
          type="submit"
          fullWidth
          disabled={uploading}
        >
          Submit
        </Button>
      </Grid>
      <SuccessModal countdown={5} open={submitSuccess} />
      <Snackbar
        open={submitError}
        onClose={() => setSubmitError(false)}
        autoHideDuration={6000}
      >
        <Alert severity="error">
          {typeof submitError === 'string'
            ? submitError
            : `Unable to ${
                existingClient ? 'edit' : 'add new'
              } Client. Please try again later.`}
        </Alert>
      </Snackbar>
      <ErrorModal
        open={noMatchingGeocode}
        noAutoClose
        onClose={() => {
          setNoMatchingGeocode(false)
        }}
        message="We couldn’t locate this address. Please check the postcode and address details to ensure crews can be routed correctly."
        title="Address not found"
        icon={TravelExplore}
      />
    </Grid>
  )
}

export default AddNewClient
